import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Topic } from "@/utils/data";

export default function HeroCarousel({ topics }: { topics: Topic[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % topics.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [topics.length]);

  const goPrev = () => setActive((prev) => (prev - 1 + topics.length) % topics.length);
  const goNext = () => setActive((prev) => (prev + 1) % topics.length);

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {topics.map((topic, index) => (
        <div
          key={topic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} 自慰健康生活`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/45 to-primary-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8">
          <div key={active} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs backdrop-blur mb-5">
              <i className="ri-git-repository-line"></i>
              精选专题
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {topics[active].title}
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-background-100/90 leading-relaxed max-w-xl">
              {topics[active].intro}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/topic/${topics[active].id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                进入专题
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/10 text-background-50 text-sm border border-background-50/30 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer backdrop-blur"
              >
                <i className="ri-search-line"></i>
                查找资讯
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {topics.map((topic, index) => (
          <button
            key={topic.id}
            type="button"
            aria-label={`切换到 ${topic.title}`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-background-50" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一个专题"
        onClick={goPrev}
        className="hidden md:flex absolute z-10 left-4 lg:left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个专题"
        onClick={goNext}
        className="hidden md:flex absolute z-10 right-4 lg:right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}